import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonData, CommonDataType } from '@models/common';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnRegexHandler, createStateResetHandler, getOne, StateDictionary } from '../helpers';
import { CommonDataService } from './common-data.service';

const sortByName = (a: CommonData, b: CommonData): number => a.details.name.localeCompare(b.details.name);

type CommonDataState = {
  commonData: CommonData[];
  isLoading: boolean;
  selectedCommonData: CommonData | null;
  selectedCountry: CommonData | null;
  selectedCommonDataLoading: boolean;
};

const initialState: CommonDataState = {
  commonData: [],
  selectedCountry: null,
  isLoading: false,
  selectedCommonData: null,
  selectedCommonDataLoading: false,
};

const commonDataDictionary: StateDictionary<CommonData> = {
  entityList: 'commonData',
  listLoading: 'isLoading',
  selectedEntity: 'selectedCommonData',
  selectedEntityLoading: 'selectedCommonDataLoading',
  entityId: 'id',
  createLoading: '',
  sortFn: (a, b) => a.type.localeCompare(b.type),
};

export const CommonDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('common-data-store'),

  withComputed(state => ({
    selectedCommonDataId: computed(() => state.selectedCommonData()?.id || null),

    audiences: computed(() => [...state.commonData().filter(item => item.type === 'audience')].sort(sortByName)),
    regions: computed(() => [...state.commonData().filter(item => item.type === 'region')].sort(sortByName)),
    membershipLevels: computed(() => [...state.commonData().filter(item => item.type === 'membershipLevel')].sort(sortByName)),
    languages: computed(() => [...state.commonData().filter(item => item.type === 'language')].sort(sortByName)),
    countries: computed(() => [...state.commonData().filter(item => item.type === 'country')].sort(sortByName)),
    workloads: computed(() => [...state.commonData().filter(item => item.type === 'workload')].sort(sortByName)),
    states: computed(() => [...state.commonData().filter(item => item.type === 'state')].sort(sortByName)),
  })),

  withMethods((store, commonDataService = inject(CommonDataService), router = inject(Router)) => {
    createStateResetHandler<CommonDataState>(['/common-data', '/marketing'])(store, initialState);
    createCustomStateSetOnRegexHandler<CommonDataState>(new RegExp(`^/common-data/(common-data|regions)$`))(store, {
      selectedCommonData: null,
    });

    const repositoryMethods = {
      getAll: rxMethod<CommonDataType | undefined>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(type =>
            commonDataService.getAll(type).pipe(
              tap(commonData => {
                let currentCommonData = [...store.commonData()];
                commonData.forEach(cd => {
                  if (!currentCommonData.find(c => c.id === cd.id)) {
                    currentCommonData.push(cd);
                  }
                });
                patchState(store, { commonData: currentCommonData, isLoading: false });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getOne: getOne<CommonData>({
        store,
        service: commonDataService,
        dictionary: commonDataDictionary,
      }),
    };

    const selectCountry = (countryId: string): void => {
      const country = store.commonData().find(cd => cd.id === countryId);
      patchState(store, { selectedCountry: country });
      if (!country) {
        commonDataService.getOne(countryId).subscribe(countryData => {
          patchState(store, { selectedCountry: countryData });
        });
      }
    };

    const selectCommonData = (id: string): CommonData | null => {
      const selected = store.commonData().find(cd => cd.id === id) || null;
      patchState(store, { selectedCommonData: selected });
      if (!selected) {
        repositoryMethods.getOne(id);
        return null;
      }
      return selected;
    };

    const commonRoutes: Record<CommonDataType, (parentId?: string) => string> = {
      country: () => 'common-data/countries',
      region: () => 'common-data/regions',
      language: () => 'common-data/languages',
      membershipLevel: () => 'common-data/membership-levels',
      audience: () => 'marketing/categories',
      workload: () => 'common-data/workloads',
      state: (parentId?: string) => (parentId ? `common-data/countries/${parentId}/states` : 'common-data/states'),
      others: () => '',
    };

    const navigationActions = {
      goToSpecificTypeActions: async (id: string, type: CommonDataType, parentId?: string): Promise<boolean> => {
        const route = commonRoutes[type](parentId);
        const result = await router.navigate([`/${route}`, id]);
        if (!result) return false;

        if (!store.selectedCommonDataLoading()) repositoryMethods.getOne(id);
        return true;
      },

      goToListPage: async (type: CommonDataType, parentId?: string): Promise<boolean> => {
        const route = commonRoutes[type](parentId);
        const result = await router.navigate([`/${route}`]);
        if (!result) return false;
        patchState(store, {
          selectedCommonData: null,
        });
        return true;
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
      selectCommonData,
      selectCountry,
      resetState: (state: Partial<CommonDataState>) => patchState(store, state),
    };
  }),
);
export interface CommonDataStoreType {
  readonly commonData: Signal<CommonData[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedCommonData: Signal<CommonData | null>;
  readonly selectedCountry: Signal<CommonData | null>;
  readonly selectedCommonDataLoading: Signal<boolean>;

  readonly selectedCommonDataId: Signal<string | null>;
  readonly audiences: Signal<CommonData[]>;
  readonly regions: Signal<CommonData[]>;
  readonly membershipLevels: Signal<CommonData[]>;
  readonly languages: Signal<CommonData[]>;
  readonly countries: Signal<CommonData[]>;
  readonly workloads: Signal<CommonData[]>;
  readonly states: Signal<CommonData[]>;

  readonly getAll: (type?: CommonDataType) => void;
  readonly getOne: (id: string) => void;
  readonly selectCommonData: (id: string) => CommonData | null;
  readonly selectCountry: (countryId: string) => void;
  readonly goToSpecificTypeActions: (id: string, type: CommonDataType, parentId?: string) => Promise<boolean>;
  readonly goToListPage: (type: CommonDataType, parentId?: string) => Promise<boolean>;
  readonly resetState: (state: Partial<CommonDataState>) => void;
}
